import { h, defineComponent, ref } from 'vue';
import {
  inspectionPhotosExportUrl,
  inspectionReportExportUrl,
  SupportedExportFormat,
  SupportedExportFormatEnum,
} from '@/services/api/resources/inspection/exports';
import {
  InspectionReportExport as BaseInspectionReportExport,
} from '@/ui/pages/inspection/export/InspectionReportExport';
import { legacyRecord } from '@/entities/inspection/model';
import { syncRefWithTaskState, useTask } from '@/ui/concerns';
import { user } from '@/ui/auth/auth';
import { CLICK_EVENTS, trackClick } from '@/services/activityTracker/activityTrackerHelper';
import { download } from '@/ui/helpers';
import { useInspectionExport } from '@/ui/pages/inspection/useInspectionExport';

interface InspectionReportProps {
  record: legacyRecord;
}

export const InspectionReportExport = defineComponent<InspectionReportProps>({
  name: 'InspectionReportExport',
  props: ['record'],
  setup(props) {
    const loading = ref(false);

    const { saveFileFromUrl } = useInspectionExport();

    const { run: exportPhotos } = useTask(
      () => inspectionPhotosExportUrl(
        props.record.inspection_id!,
        SupportedExportFormatEnum.ZIP,
        user.value.timezone,
      ),
      url => {
        trackClick(CLICK_EVENTS.EXPORT_PHOTOS);
        download(url);
      },
    );

    const exportReport = (format: SupportedExportFormat) => {
      const { run } = useTask(
        () => inspectionReportExportUrl(
          props.record.inspection_id!,
          format,
          user.value.timezone,
        ),
        url => {
          trackClick(CLICK_EVENTS.EXPORT_PDF);
          const task = saveFileFromUrl(url.href);
          syncRefWithTaskState(loading, task);
        },
      );

      return run;
    };

    return function render() {
      return h('div', [
        h(BaseInspectionReportExport, {
          props: {
            hasPhotos: props.record.photos_url,
            hasPdf: props.record.pdf_url,
            loading: loading.value,
            tooltiped: true,
            unlabeled: true,
            formats: [
              SupportedExportFormatEnum.CSV,
              SupportedExportFormatEnum.XLSX,
              SupportedExportFormatEnum.DOCX,
              SupportedExportFormatEnum.XLSX_SIMPLE,
            ],
          },
          on: {
            export: ({ format }: { format: SupportedExportFormat | 'photos' }) => {
              if (format === 'photos') {
                exportPhotos();
              } else {
                exportReport(format)();
              }
            },
          },
        }),
      ]);
    };
  },
});

export const inspectionReportExport = (
  { props }: { props: InspectionReportProps },
) => h(InspectionReportExport, { props });
